import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

export interface OrganizationDto {
  id: number;
  helcimCustomerId?: number | string;
  legalName: string;
  billingBusinessName: string;
  shippingBusinessName: string;
  displayName: string;
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;
  billingAddress1: string;
  billingAddress2?: string;
  billingCity: string;
  billingState: string;
  billingCountry: string;
  billingZipCode: string;
  contactName: string;
  contactEmail: string;
  shippingAddress1: string;
  shippingAddress2?: string;
  shippingContactName: string;
  shippingContactEmail: string;
  shippingContactPhone: string;
  shippingCity: string;
  shippingState: string;
  shippingCountry: string;
  shippingZipCode: string;
  externalKey?: string;
  status?: string;
  isEnabled?: boolean;
  isDeleted?: boolean;
}

export interface SetEnabledDto {
  isEnabled: boolean;
}

export interface OrganizationCreateDto {
  legalName: string;
  billingBusinessName: string;
  shippingBusinessName: string;
  displayName: string;
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;
  billingAddress1: string;
  billingAddress2?: string;
  billingCity: string;
  billingState: string;
  billingCountry: string;
  billingZipCode: string;
  contactName: string;
  contactEmail: string;
  shippingAddress1: string;
  shippingAddress2?: string;
  shippingContactName: string;
  shippingContactEmail: string;
  shippingContactPhone: string;
  shippingCity: string;
  shippingState: string;
  shippingCountry: string;
  shippingZipCode: string;
  externalKey?: string;
  status?: string;
}

export interface OrganizationUpdateDto {
  legalName: string;
  billingBusinessName: string;
  shippingBusinessName: string;
  displayName: string;
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;
  billingAddress1: string;
  billingAddress2?: string;
  billingCity: string;
  billingState: string;
  billingCountry: string;
  billingZipCode: string;
  contactName: string;
  contactEmail: string;
  shippingAddress1: string;
  shippingAddress2?: string;
  shippingContactName: string;
  shippingContactEmail: string;
  shippingContactPhone: string;
  shippingCity: string;
  shippingState: string;
  shippingCountry: string;
  shippingZipCode: string;
  externalKey?: string;
  status?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api/organizations';

  /**
   * Get all organizations (SiteAdmin only)
   */
  getAll(): Observable<OrganizationDto[]> {
    return this.http.get<OrganizationDto[]>(this.apiUrl);
  }

  /**
   * Get archived organizations (SiteAdmin only)
   */
  getArchived(): Observable<OrganizationDto[]> {
    return this.http.get<OrganizationDto[]>(`${this.apiUrl}/archived`);
  }

  /**
   * Get organization by ID
   */
  getById(id: number): Observable<OrganizationDto> {
    return this.http.get<OrganizationDto>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get organization for current authenticated user
   */
  getMyOrganization(): Observable<OrganizationDto> {
    return this.http.get<OrganizationDto>(`${this.apiUrl}/me`);
  }

  /**
   * Get organization with users and roles
   */
  getWithDetails(id: number): Observable<unknown> {
    return this.http.get<unknown>(`${this.apiUrl}/${id}/details`);
  }

  /**
   * Create new organization (SiteAdmin only)
   */
  create(data: OrganizationCreateDto): Observable<OrganizationDto> {
    return this.http.post<OrganizationDto>(this.apiUrl, data);
  }

  /**
   * Update organization (SiteAdmin/OrgAdmin)
   */
  update(id: number, data: OrganizationUpdateDto): Observable<OrganizationDto> {
    return this.http.put<OrganizationDto>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Delete organization (SiteAdmin only)
   */
  delete(id: number): Observable<unknown> {
    return this.http.delete<unknown>(`${this.apiUrl}/${id}`);
  }

  /**
   * Hard delete organization (SiteAdmin only, requires soft-deleted + disabled state)
   */
  hardDelete(id: number): Observable<unknown> {
    return this.http.delete<unknown>(`${this.apiUrl}/${id}/hard`);
  }

  /**
   * Restore archived organization (SiteAdmin only)
   */
  restore(id: number): Observable<OrganizationDto> {
    return this.http.put<OrganizationDto>(`${this.apiUrl}/${id}/restore`, {});
  }

  /**
   * Enable/disable organization (SiteAdmin only)
   */
  setEnabled(id: number, data: SetEnabledDto): Observable<OrganizationDto> {
    return this.http.put<OrganizationDto>(`${this.apiUrl}/${id}/enabled`, data);
  }
}
